import React, { useState } from 'react';
import { Button, Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BsSearch } from 'react-icons/bs';

import { applyCustomerSelection, pickerListFor } from './orders-customer-pickers';
import { OrderType } from './orders-types';

/**
 * Customer select control for the Order forms. Shows the current customer name
 * and a button that opens the shared customer-list datagrid (search + filters,
 * same picker as Misc Billing) in a modal — the grid is too large to sit inline.
 * Selecting a row writes customerId/customerName + the customer's defaults and
 * closes the modal.
 */
export const OrderCustomerPickerButton: React.FC<any> = ({ watch, setValue, register, errors, required }) => {
  const [show, setShow] = useState(false);
  const orderTypeId = Number(watch?.('orderTypeId'));
  const customerName = watch?.('customerName');
  const customerId = watch?.('customerId');
  const customerError = errors?.customerId;
  const List = pickerListFor(orderTypeId);

  // Legacy PopulateOrderInfo: on a placed order (OrderNumber > 0) the customer
  // picker is disabled (ddlCustomer.Enabled = false); for Dewar it is also hidden
  // (btnSelectDewarCustomer.Visible = false). New/unplaced orders stay editable.
  const orderNumber = watch?.('orderNumber');
  const isPlaced = !!orderNumber && Number(orderNumber) > 0;
  const hidePicker = isPlaced && orderTypeId === OrderType.Dewar;

  // A customer can only be picked once an order type is chosen (the picker list
  // and customer defaults are order-type specific).
  const needsOrderType = !orderTypeId;
  const pickerDisabled = isPlaced || needsOrderType;

  return (
    <Form.Group className="mb-1">
      <Form.Label>Customer{required && <span className="text-danger" aria-hidden="true"> *</span>}</Form.Label>
      <input
        type="hidden"
        {...register('customerId', {
          validate: (value: any) => !required || (!!value && Number(value) !== 0) || 'Please select a customer',
        })}
      />
      <div className="d-flex align-items-center gap-2">
        <span className="text-truncate fw-bold">
          {customerName ? (
            <>
              {customerName}
              {!!customerId && Number(customerId) !== 0 && ` (${customerId})`}
            </>
          ) : (
            <em className="text-muted fw-normal">No customer selected</em>
          )}
        </span>
        {!hidePicker && (
          needsOrderType ? (
            <OverlayTrigger
              placement="top"
              container={typeof document !== 'undefined' ? document.body : undefined}
              popperConfig={{ strategy: 'fixed' }}
              overlay={<Tooltip id="customer-picker-tooltip">Select an order type first</Tooltip>}
            >
              <span className="flex-shrink-0 d-inline-block">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  <BsSearch className="me-1" />
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              variant="outline-secondary"
              size="sm"
              className="flex-shrink-0"
              disabled={pickerDisabled}
              onClick={() => setShow(true)}
            >
              <BsSearch className="me-1" />
            </Button>
          )
        )}
      </div>
      {customerError && (
        <Form.Control.Feedback type="invalid" className="d-block">
          {customerError.message || 'Please select a customer'}
        </Form.Control.Feedback>
      )}

      <Modal show={show} onHide={() => setShow(false)} size="xl" scrollable centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: 520 }}>
          {show && (
            <List
              // Swallow the list's pk write; selection is applied via onSelection.
              fieldProps={{ setValue: () => {}, field: { name: 'customerPick' } }}
              onSelection={async (sel: any) => {
                if (!sel?.length) return;
                await applyCustomerSelection(orderTypeId, sel, setValue);
                setShow(false);
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </Form.Group>
  );
};
